import React, { useEffect, useState } from 'react';
import { Button, Container, FormControl, FormLabel, Input, Stack, Typography } from '@mui/joy';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { Course, Hole } from '../models/course';
import { serviceFacade } from '../services/service-facade';
import { useAuth } from '../auth/AuthContext';

interface CreateCourseLoaderData {
    course?: Course;
}

interface LocationState {
    name?: string;
    holes?: Hole[];
}

// Par is kept as a string while editing so the field can be cleared (empty)
// without snapping to 0; it is parsed back to a number at submit time.
interface HoleInput {
    number: number;
    par: string;
}

const DEFAULT_PAR = 3;
const DEFAULT_HOLE_COUNT = 9;

function buildHoles(count: number, existing: HoleInput[] = []): HoleInput[] {
    return Array.from({ length: count }, (_, i) => {
        const hole = existing.find((h) => h.number === i + 1);
        return { number: i + 1, par: hole?.par ?? String(DEFAULT_PAR) };
    });
}

const CreateCourse = () => {
    const loaderData = useLoaderData() as CreateCourseLoaderData | null;
    const location = useLocation();
    const navigate = useNavigate();
    const { userId, playerId, loading: authLoading } = useAuth();

    const prefill = (location.state as LocationState) ?? {};
    const existing = loaderData?.course;

    const initialName = existing?.name ?? prefill.name ?? '';
    const initialHoles = existing?.holes ?? prefill.holes ?? buildHoles(DEFAULT_HOLE_COUNT);

    const [courseName, setCourseName] = useState(initialName);
    const [holeCount, setHoleCount] = useState(String(initialHoles.length || DEFAULT_HOLE_COUNT));
    const [holes, setHoles] = useState<HoleInput[]>(
        initialHoles.length > 0
            ? initialHoles.map((h) => ({ number: h.number, par: String(h.par) }))
            : buildHoles(DEFAULT_HOLE_COUNT)
    );
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const count = parseInt(holeCount, 10);
        if (Number.isNaN(count) || count < 1 || count > 36) return;
        setHoles((prev) => buildHoles(count, prev));
    }, [holeCount]);

    useEffect(() => {
        if (!authLoading && !userId) navigate('/login');
    }, [authLoading, userId, navigate]);

    const handleParChange = (holeNumber: number, par: string) => {
        setHoles((prev) => prev.map((h) => (h.number === holeNumber ? { ...h, par } : h)));
    };

    const handleSubmit = async () => {
        if (!playerId) return;
        try {
            setIsLoading(true);
            const holesToSave: Hole[] = holes.map((h) => {
                const par = parseInt(h.par, 10);
                return { number: h.number, par: Number.isNaN(par) ? DEFAULT_PAR : par };
            });
            if (existing) {
                await serviceFacade.updateCourse(existing.id, { name: courseName, holes: holesToSave }, playerId);
            } else {
                await serviceFacade.createCourse(courseName, holesToSave, playerId);
            }
            navigate('/courses');
        } finally {
            setIsLoading(false);
        }
    };

    if (authLoading || !userId) return null;

    return (
        <Container>
            <Stack spacing={4}>
                <Typography level="h2">{existing ? 'Edit Course' : 'New Course'}</Typography>

                <FormControl>
                    <FormLabel>Course Name</FormLabel>
                    <Input
                        placeholder="Enter course name"
                        value={courseName}
                        onChange={(e) => setCourseName(e.target.value)}
                    />
                </FormControl>

                <FormControl>
                    <FormLabel>Number of Holes</FormLabel>
                    <Input
                        type="number"
                        slotProps={{ input: { min: 1, max: 36 } }}
                        value={holeCount}
                        onChange={(e) => setHoleCount(e.target.value)}
                    />
                </FormControl>

                <Stack spacing={1}>
                    <Typography level="title-md">Par per Hole</Typography>
                    {holes.map((hole) => (
                        <Stack key={hole.number} direction="row" alignItems="center" spacing={2}>
                            <Typography sx={{ minWidth: 60 }}>Hole {hole.number}</Typography>
                            <FormControl>
                                <Input
                                    aria-label={`Par for hole ${hole.number}`}
                                    type="number"
                                    slotProps={{ input: { min: 1, max: 10 } }}
                                    value={hole.par}
                                    onChange={(e) => handleParChange(hole.number, e.target.value)}
                                    sx={{ width: 80 }}
                                />
                            </FormControl>
                        </Stack>
                    ))}
                </Stack>

                <Button onClick={handleSubmit} loading={isLoading} disabled={isLoading || !courseName.trim()}>
                    Save Course
                </Button>
            </Stack>
        </Container>
    );
};

export default CreateCourse;
